<template>
  <main class="front-page-prototype single-film-prototype">
    <section class="front-page-hero hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="frontPageBackgroundImage"
        :mobile-background-src="frontPageMobileBackgroundImage"
        login-path="/login?redirect=%2F"
        content-class="front-page-hero__content"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid front-page-copy-row">
          <div class="col g-0">
            <section class="front-page-copy" aria-label="Filmsomniac introduction">
              <p class="front-page-copy__primary" data-testid="front-page-copy-primary">
                Filmsomniac is a tool<br />
                to keep track of<br
                  class="front-page-copy__primary-break-780"
                  data-testid="front-page-copy-primary-break-780"
                />
                what you’ve watched.
              </p>
              <p class="front-page-copy__secondary" data-testid="front-page-copy-secondary">
                Movies and TV shows.
              </p>
              <p class="front-page-copy__tagline" data-testid="front-page-copy-tagline">
                It's your
                <a
                  class="front-page-copy__tagline-link"
                  data-testid="front-page-movie-diary-link"
                  href="/movie-diary"
                >movie diary</a>. A second brain.
              </p>
              <p
                class="front-page-copy__tagline front-page-copy__postscript"
                data-testid="front-page-copy-postscript"
              >
                And of course, you can build lists,
                <span class="front-page-copy__postscript-break" data-testid="front-page-copy-postscript-break">rate and favourite things.</span>
              </p>
            </section>
            <section class="front-page-backdrops-section" aria-label="Popular titles">
              <a
                class="front-page-signup-cta"
                data-testid="front-page-signup-cta"
                href="/sign-up"
              >Register! - It's free!</a>
              <div class="front-page-backdrops-intro" data-testid="front-page-backdrops-intro">
                <p
                  class="front-page-backdrops-intro__title"
                  data-testid="front-page-backdrops-intro-title"
                >
                  Not sure yet?
                </p>
                <p
                  class="front-page-backdrops-intro__copy"
                  data-testid="front-page-backdrops-intro-copy"
                >
                  Check out these and other popular titles
                </p>
              </div>
              <div class="front-page-backdrops" data-testid="front-page-backdrops">
                <a
                  v-for="backdrop in frontPageBackdrops"
                  :key="backdrop.src"
                  class="front-page-backdrops__link"
                  data-testid="front-page-backdrop-link"
                  :href="backdrop.href"
                  :style="{ gridRow: backdrop.row, gridColumn: backdrop.column }"
                >
                  <img
                    class="front-page-backdrops__image"
                    data-testid="front-page-backdrop-image"
                    :src="backdrop.src"
                    :alt="backdrop.alt"
                  />
                  <span class="front-page-backdrops__overlay">
                    <span class="front-page-backdrops__title" data-testid="front-page-backdrop-title">
                      {{ backdrop.title }} <span class="front-page-backdrops__years">({{ backdrop.years }})</span>
                    </span>
                  </span>
                </a>
              </div>
              <p class="front-page-discover-more">
                <a
                  class="front-page-discover-more__link"
                  data-testid="front-page-discover-more"
                  href="/discover"
                >Discover more -&gt;</a>
              </p>
              <div class="front-page-account-preview" data-testid="front-page-account-preview">
                <p
                  class="front-page-account-preview__title"
                  data-testid="front-page-account-preview-title"
                >
                  Or take a look inside a real Filmsomniac account
                </p>
                <p
                  class="front-page-account-preview__copy"
                  data-testid="front-page-account-preview-copy"
                >
                  An account with watches, ratings and favourites.
                </p>
                <a
                  class="front-page-account-preview__image-link"
                  data-testid="front-page-account-preview-image-link"
                  href="/filmsomniacs/demo-account"
                >
                  <img
                    class="front-page-account-preview__image"
                    data-testid="front-page-account-preview-image"
                    :src="demoAccountImage"
                    alt="Filmsomniac demo account preview"
                  />
                </a>
                <p class="front-page-account-preview__link-row">
                  <a
                    class="front-page-account-preview__link"
                    data-testid="front-page-account-preview-link"
                    href="/filmsomniacs/demo-account"
                  >Take a look -&gt;</a>
                </p>
                <p class="front-page-account-preview__signup-row">
                  <a
                    class="front-page-signup-cta"
                    data-testid="front-page-account-preview-signup-cta"
                    href="/sign-up"
                  >Register! - It's free!</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </HeroTopPanel>
    </section>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import { ref } from 'vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/front-page/styles.css'

const logoPath = `${import.meta.env.BASE_URL}branding/logo.png`
const frontPageBackgroundImage = `${import.meta.env.BASE_URL}branding/front-page-bg.webp`
const frontPageMobileBackgroundImage = `${import.meta.env.BASE_URL}branding/front-page-bg-mobile.webp`
const demoAccountImage = `${import.meta.env.BASE_URL}branding/demo-account.webp`
const frontPageBackdrops = [
  {
    row: 1,
    column: 1,
    title: 'Game of Thrones',
    years: '2011 - 2019',
    href: '/shows/game-of-thrones',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/game-of-thrones.jpg`,
    alt: 'Game of Thrones backdrop',
  },
  {
    row: 1,
    column: 2,
    title: 'Project Hail Mary',
    years: '2026',
    href: '/films/project-hail-mary',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/project-hail-mary.jpg`,
    alt: 'Project Hail Mary backdrop',
  },
  {
    row: 1,
    column: 3,
    title: 'Friends',
    years: '1994 - 2004',
    href: '/shows/friends',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/friends.jpg`,
    alt: 'Friends backdrop',
  },
  {
    row: 2,
    column: 1,
    title: 'The Dark Knight',
    years: '2008',
    href: '/films/the-dark-knight',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/the-dark-knight.jpg`,
    alt: 'The Dark Knight backdrop',
  },
  {
    row: 2,
    column: 2,
    title: 'The Pitt',
    years: '2025 -',
    href: '/shows/the-pitt',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/the-pitt.jpg`,
    alt: 'The Pitt backdrop',
  },
  {
    row: 2,
    column: 3,
    title: 'Titanic',
    years: '1997',
    href: '/films/titanic',
    src: `${import.meta.env.BASE_URL}branding/frontpage-backdrops/titanic.jpg`,
    alt: 'Titanic backdrop',
  },
]
const mobileSearchOpen = ref(false)

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}
</script>
